import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams, useSearchParams} from 'react-router-dom';
import {useContestLogic} from '@/hooks/useContestLogic.js';
import {APP_STEPS} from '@/hooks/contest/contestSteps.js';
import {APP_STATUS} from '@/hooks/contest/appStatus.js';
import {MissedView} from '@/features/contest/MissedView.jsx';
import {StepQuizz} from '@/features/contest/StepQuizz.jsx';
import ModalPlayerDetail from '@/components/ModalPlayerDetail.jsx';
import QrCodeOverlay from '@/components/overlays/QrCodeOverlay.jsx';
import QuizBoostOverlay from '@/components/overlays/QuizBoostOverlay.jsx';
import HalfTimeRewardOverlay from '@/components/overlays/HalfTimeRewardOverlay.jsx';
import ZoneProfilingOverlay from '@/components/overlays/ZoneProfilingOverlay.jsx';
import GoogleReviewOverlay from '@/components/overlays/GoogleReviewOverlay.jsx';
import {trackEvent} from '@/api/participant.js';
import {TRACK_EVENT} from '@/utils/constants.js';
import {LoadingScreen} from '@/components/LoadingScreen.jsx';
import {ErrorScreen} from '@/components/ErrorScreen.jsx';
import {StepBettingFootball} from '@/features/contest/StepBettingFootball.jsx';
import {StepForm} from '@/features/contest/StepForm.jsx';
import {StepLeaderboard} from '@/features/contest/StepLeaderboard.jsx';
import {LeaderboardTabs} from '@/features/contest/LeaderboardTabs.jsx';
import {StepBettingHalfTime} from '@/features/contest/StepBettingHalfTime.jsx';
import {StepBettingHalfTimeRugby} from '@/features/contest/StepBettingHalfTimeRugby.jsx';
import {HalfTimeUpsellModal} from '@/features/contest/HalfTimeUpsellModal.jsx';
import {buildHalfTimeView} from '@/utils/leaderboardEngine.js';
import {GameLayout} from '@/features/contest/GameLayout.jsx';
import {Footer} from '@/components/Footer.jsx';
import {StepResult} from "@/features/contest/StepResult.jsx";
import {StepWelcome} from "@/features/contest/StepWelcome.jsx";
import {StepWelcomePenalty} from "@/features/contest/StepWelcomePenalty.jsx";
import {ReferralWelcomeModal} from "@/features/contest/ReferralWelcomeModal.jsx";
import {StepPenaltyResult} from "@/features/contest/StepPenaltyResult.jsx";
import {StepBettingRugby} from "@/features/contest/StepBettingRugby.jsx";
import {PenaltyShootout} from "@/features/contest/PenaltyShootout.jsx";
import {PenaltyUpsellModal} from "@/features/contest/PenaltyUpsellModal.jsx";
import {usePenaltyShootoutView} from "@/hooks/contest/usePenaltyShootoutView.js";

export const ContestPage = () => {
    const {slug, contestId} = useParams();
    const [searchParams] = useSearchParams();
    const {t} = useTranslation();

    const {game, ui, user, leaderboard, overlays, halfTime, penalty, initContest, refreshStatus} = useContestLogic();
    const {step} = ui;

    const contestType = game?.data?.type;

    // Tirs au but : on poll /me ; le joueur est sur l'écran résultat FT, et ENTRE dans la séance en
    // opt-in (modale d'incitation OU encart). L'overlay ne s'affiche pas en auto-takeover.
    const penaltyView = usePenaltyShootoutView({
        penaltyState: penalty.state,
        penaltyBoard: penalty.board,
        homeTeam: game.data?.fixture?.homeTeam,
        awayTeam: game.data?.fixture?.awayTeam,
        onPredict: penalty.submit,
    });
    const penaltyPhase = Boolean(penalty.state && !penalty.state.finished);
    const [penaltyOpenState, setPenaltyOpen] = useState(false);
    // Derived (not an effect): a player who already played a kick resumes the shootout immediately on
    // reload — no one-frame flash of the result screen, and the upsell is suppressed (penaltyOpen true).
    const penaltyOpen = penaltyOpenState || Boolean(penalty.state?.hasJoined);
    const [showPenaltyUpsell, setShowPenaltyUpsell] = useState(false);
    const [penaltyUpsellDismissed, setPenaltyUpsellDismissed] = useState(false);
    // Latecomer NON inscrit qui arrive pendant la séance (pas de participation → pas de penalty.state) :
    // mini-tunnel dédié (welcome pénos → form pseudo/email/captcha → join pénos), sans le prono FT.
    const isLatecomerPenalty = !penalty.state
        && game.data?.fixture?.state === 'PENALTY_SHOOTOUT'
        && Boolean(game.data?.participantUi?.penaltyRewardLabel);
    const [latecomerStep, setLatecomerStep] = useState('welcome');
    const [latecomerLoading, setLatecomerLoading] = useState(false);
    const [latecomerError, setLatecomerError] = useState(null);
    const handlePenaltyJoin = async (e, extra) => {
        setLatecomerLoading(true);
        setLatecomerError(null);
        try {
            await penalty.join({pseudo: user.info.pseudo, email: user.info.email, captchaToken: extra?.captchaToken});
            setPenaltyOpen(true); // bascule directe sur l'overlay (pas d'écran « perdu »)
        } catch (err) {
            setLatecomerError(err);
        } finally {
            setLatecomerLoading(false);
        }
    };
    useEffect(() => {
        if (penaltyPhase && !penaltyUpsellDismissed && !penaltyOpen) {
            setShowPenaltyUpsell(true);
        }
    }, [penaltyPhase, penaltyUpsellDismissed, penaltyOpen]);

    // Mi-temps jouable ? (sur le classement, avant la pause, pas encore joué).
    // "betDisplayHalfTime" (= prono mi-temps déjà posé) ne vit que sur la ligne leaderboard du joueur,
    // chargée APRÈS le rendu initial au refresh. Tant que cette ligne n'est pas là, on ne SAIT pas si le
    // prono a été posé : on doit donc attendre qu'elle soit chargée (leaderboardReady) avant d'autoriser
    // l'ouverture de la modale, sinon faux positif → la modale s'ouvre alors que le prono est déjà fait.
    const fixtureState = game.data?.fixture?.state;
    const beforeBreak = fixtureState === 'NOT_STARTED' || fixtureState === 'FIRST_HALF';
    const myLeaderboardRow = (leaderboard.fullList || []).find(r => r.isMe);
    const leaderboardReady = Boolean(myLeaderboardRow);
    const playedHalfTime = myLeaderboardRow?.betDisplayHalfTime != null;
    const canPlayHalfTime = halfTime.enabled && leaderboardReady && !playedHalfTime && beforeBreak;

    // Modale d'incitation mi-temps : s'ouvre AU-DESSUS du classement (comme le quiz), une fois inscrit.
    // Rejetée → on ne la réaffiche plus (le bandeau du classement reste pour rejouer).
    const [showHalfTimeUpsell, setShowHalfTimeUpsell] = useState(false);
    const [halfTimeUpsellDismissed, setHalfTimeUpsellDismissed] = useState(false);
    // Referral confirmation toast: shown once after a sign-up that created an attribution.
    const [referralToastDismissed, setReferralToastDismissed] = useState(false);
    // Referral welcome modal: shown once, for a new visitor arriving via a valid referral link.
    // Org gate: the code stays in sessionStorage for the whole tab session, so without matching the
    // resolution's org against the CURRENT page, bar A's promise would pop over bar B's contest
    // (and promise a reward the backend would silently reject).
    const [referralModalDismissed, setReferralModalDismissed] = useState(false);
    const referralMatchesOrg = user.referral?.organizationSlug != null && user.referral.organizationSlug === slug;
    const showReferralModal = Boolean(user.referral?.valid) && referralMatchesOrg && user.isAnonymous && !referralModalDismissed;

    // Built once so the two welcome surfaces (normal + penalty latecomer) share the exact same props.
    const referralModal = (extraOpenCondition = true) => (
        <ReferralWelcomeModal
            isOpen={showReferralModal && extraOpenCondition}
            rewardTitle={user.referral?.refereeRewardTitle}
            gameRewardLabel={game.data?.participantUi?.rewardLabel}
            onClose={() => setReferralModalDismissed(true)}
        />
    );
    useEffect(() => {
        if (step === APP_STEPS.LEADERBOARD && canPlayHalfTime && !halfTimeUpsellDismissed) {
            setShowHalfTimeUpsell(true);
        }
    }, [step, canPlayHalfTime, halfTimeUpsellDismissed]);
    // Filet : si la condition tombe pendant que la modale est ouverte (leaderboard chargé révélant un
    // prono déjà posé, ou pause sifflée), on la referme — la modale ne peut jamais "survivre" à canPlayHalfTime.
    useEffect(() => {
        if (showHalfTimeUpsell && !canPlayHalfTime) setShowHalfTimeUpsell(false);
    }, [showHalfTimeUpsell, canPlayHalfTime]);

    // Overlay incitatif quiz : s'ouvre à l'arrivée sur le leaderboard si le quiz est jouable.
    // Séquencé APRÈS la mi-temps (ordre mi-temps → quiz) : on attend que la modale HT soit traitée.
    // Une fois rejeté ("Plus tard"), on ne le réaffiche plus (le bandeau reste pour rejouer).
    const [showQuizBoost, setShowQuizBoost] = useState(false);
    const [quizBoostDismissed, setQuizBoostDismissed] = useState(false);
    useEffect(() => {
        const halfTimeUpsellPending = canPlayHalfTime && !halfTimeUpsellDismissed;
        // Wait for the leaderboard row to load before deciding: until then canPlayHalfTime is unknowable
        // (false by default), so the quiz boost must not jump ahead of a half-time upsell that is about
        // to become pending. The penalty flow (latecomer onboarding or the live shootout) is not
        // concerned by the quiz — never raise the quiz boost while it is on.
        if (step === APP_STEPS.LEADERBOARD && leaderboardReady && ui.canPlayQuiz && !quizBoostDismissed
            && !halfTimeUpsellPending && !penaltyPhase && !isLatecomerPenalty) {
            setShowQuizBoost(true);
        }
    }, [step, leaderboardReady, ui.canPlayQuiz, quizBoostDismissed, canPlayHalfTime, halfTimeUpsellDismissed, penaltyPhase, isLatecomerPenalty]);

    // Overlay lot mi-temps : s'ouvre sur le leaderboard quand le joueur a gagné un lot à la pause.
    // Dismissible (le bandeau du leaderboard permet de le rouvrir tant qu'il est récupérable).
    const [showHalfTimeReward, setShowHalfTimeReward] = useState(false);
    const [halfTimeRewardDismissed, setHalfTimeRewardDismissed] = useState(false);
    const hasHalfTimeReward = user.halfTimeReward?.status === 'AVAILABLE';
    useEffect(() => {
        if (step === APP_STEPS.LEADERBOARD && hasHalfTimeReward && !halfTimeRewardDismissed) {
            setShowHalfTimeReward(true);
        }
    }, [step, hasHalfTimeReward, halfTimeRewardDismissed]);

    // Overlay zone non-bloquant sur l'écran de résultat, si la zone n'est pas encore connue
    const [showZoneProfiling, setShowZoneProfiling] = useState(false);
    const [zoneProfilingDismissed, setZoneProfilingDismissed] = useState(false);
    useEffect(() => {
        const isResult = step === APP_STEPS.RESULT_WIN || step === APP_STEPS.RESULT_LOSE;
        const hasZones = (game.data?.profilingZones?.length || 0) > 0;
        if (isResult && hasZones && !ui.hasZone && !zoneProfilingDismissed) {
            setShowZoneProfiling(true);
        }
    }, [step, ui.hasZone, zoneProfilingDismissed, game.data]);

    // Overlay avis Google : s'ouvre une fois à l'arrivée sur l'écran résultat (avec OU sans lot),
    // dès que le bar a une fiche Google. Dismissible ; le bloc avis en bas de StepResult reste le filet.
    const [showGoogleReview, setShowGoogleReview] = useState(false);
    const [googleReviewDismissed, setGoogleReviewDismissed] = useState(false);
    const googlePlaceId = game.data?.place?.googlePlaceId;
    useEffect(() => {
        const isResult = step === APP_STEPS.RESULT_WIN || step === APP_STEPS.RESULT_LOSE;
        if (isResult && googlePlaceId && !googleReviewDismissed && !showGoogleReview) {
            setShowGoogleReview(true);
            trackEvent(game.data?.id, null, TRACK_EVENT.GOOGLE_REVIEW_MODAL_VIEW);
        }
    }, [step, googlePlaceId, googleReviewDismissed, showGoogleReview, game.data?.id]);

    useEffect(() => {
        if (slug) {
            const sourceParam = searchParams.get('src');
            const magicToken = searchParams.get('mt');
            const refParam = searchParams.get('ref');
            initContest(slug, contestId, sourceParam, magicToken, refParam);
        }
    }, [slug]);

    switch (ui.status) {
        case APP_STATUS.FETCHING:
            return <LoadingScreen/>;
        case APP_STATUS.ERROR:
            return <ErrorScreen onRetry={() => window.location.reload()}/>;
        case APP_STATUS.READY:
            break;
        default:
            return <LoadingScreen/>;
    }

    // Séance de tirs au but : si le joueur est ENTRÉ → overlay plein écran tir-par-tir.
    if (penaltyPhase && penaltyOpen && penaltyView) {
        return (
            <div className="font-sans bg-[#0B0F19] min-h-screen">
                <div className="max-w-[440px] mx-auto min-h-screen relative overflow-hidden shadow-2xl">
                    <PenaltyShootout {...penaltyView}/>
                </div>
            </div>
        );
    }

    // Séance en cours mais pas (encore) entré → écran résultat FT (la bière) + modale d'incitation +
    // encart persistant pour entrer dans la séance. (Le lot FT est déjà distribué/exposé via /me.)
    if (penaltyPhase) {
        const ftReward = leaderboard.currentUser?.reward;
        return (
            <div className="font-sans bg-slate-50 text-gray-800 min-h-screen">
                <QrCodeOverlay
                    isOpen={overlays.qr.isOpen} title={overlays.qr.title} url={overlays.qr.url}
                    onClose={() => overlays.setQr({...overlays.qr, isOpen: false})}
                />
                <PenaltyUpsellModal
                    isOpen={showPenaltyUpsell}
                    rewardLabel={game.data?.participantUi?.penaltyRewardLabel}
                    onAccept={() => { setShowPenaltyUpsell(false); setPenaltyUpsellDismissed(true); setPenaltyOpen(true); }}
                    onDecline={() => { setShowPenaltyUpsell(false); setPenaltyUpsellDismissed(true); }}
                />
                <div className="max-w-[440px] mx-auto min-h-screen flex flex-col relative overflow-x-hidden shadow-2xl bg-white/50">
                    <StepResult
                        contestId={game.data.id}
                        place={game.data.place}
                        status={ftReward ? APP_STEPS.RESULT_WIN : APP_STEPS.RESULT_LOSE}
                        reward={ftReward}
                        halfTimeReward={user.halfTimeReward}
                        penaltyReward={penalty.reward}
                        pseudo={leaderboard.currentUser?.pseudo}
                        onShowQr={overlays.setQr}
                        onShowHalfTimeReward={() => setShowHalfTimeReward(true)}
                        triggerAnimation={false}
                    />
                    <div className="px-5 pb-7 pt-1">
                        <button
                            onClick={() => setPenaltyOpen(true)}
                            className="w-full py-4 rounded-2xl bg-action-green text-white font-black text-lg active:scale-95 transition shadow-lg shadow-action-green/30"
                        >
                            ⚽ {t('penalty.play_cta', 'Jouer les tirs au but')}
                        </button>
                    </div>
                    <Footer contestType={contestType}/>
                </div>
            </div>
        );
    }

    // Latecomer : mini-tunnel pénos (welcome → form), AVANT le step machine standard.
    // Le welcome est un plein écran (comme StepWelcome) ; le form est une carte dans le cadre.
    if (isLatecomerPenalty) {
        if (latecomerStep === 'welcome') {
            return (
                <>
                    <StepWelcomePenalty
                        contestData={game.data}
                        onStart={() => setLatecomerStep('form')}
                    />
                    {referralModal()}
                </>
            );
        }
        return (
            <div className="font-sans bg-slate-50 text-gray-800 min-h-screen">
                <div className="max-w-[440px] mx-auto min-h-screen flex flex-col relative overflow-x-hidden shadow-2xl bg-white/50">
                    <GameLayout
                        contestData={game.data} slug={slug} matchScore={game.scores}
                        isGoalFlashing={ui.isGoalFlashing}
                        onTeamClick={() => {}}
                    >
                        <StepForm
                            userInfo={user.info}
                            setUserInfo={user.setInfo}
                            handleFormSubmit={handlePenaltyJoin}
                            isLoading={latecomerLoading}
                            apiError={latecomerError}
                            title={t('penalty.form_title', 'Plus que ton pseudo')}
                            description={t('penalty.form_subtitle', 'Ton pseudo + email pour jouer les tirs au but.')}
                            icon="⚽"
                            backLabel={t('penalty.form_back', 'Retour')}
                            onBack={() => setLatecomerStep('welcome')}
                        />
                    </GameLayout>
                    <Footer contestType={contestType}/>
                </div>
            </div>
        );
    }

    const handleHeaderTeamClick = (side) => {
        if (step === APP_STEPS.GAME && game.data?.type === 'RUGBY') {
            game.handleRugbyBetChange('selection', side);
            if (!game.userRugbyBet.margin) {
                game.handleRugbyBetChange('margin', 1);
            }
        }
    };

    const renderCurrentStep = () => {
        switch (step) {
            case APP_STEPS.WELCOME:
                return (
                    <StepWelcome
                        onStart={() => ui.setStep(APP_STEPS.GAME)}
                        contestData={game.data}
                        quizPoints={user.info.quizPoints}
                    />
                );

            case APP_STEPS.GAME:
                if (game.data?.type === 'RUGBY') {
                    return (
                        <StepBettingRugby
                            userBet={game.userRugbyBet}
                            fixture={game.data.fixture}
                            handleBetChange={game.handleRugbyBetChange}
                            calculations={game.calculations}
                            onValidate={user.submitProno}
                            quizPoints={user.info.quizPoints}
                        />
                    );
                }
                return (
                    <StepBettingFootball
                        userScore={game.userBet}
                        matchScore={game.scores}
                        handleScoreChange={game.handleScoreChange}
                        calculations={game.calculations}
                        onValidate={user.submitProno}
                        quizPoints={user.info.quizPoints}
                    />
                );
            case APP_STEPS.FORM:
                return (
                    <StepForm
                        userInfo={user.info}
                        setUserInfo={user.setInfo}
                        handleFormSubmit={user.submit}
                        isLoading={ui.isLoading}
                        apiError={ui.error}
                        userScore={game.userBet}
                        onBack={() => ui.setStep(APP_STEPS.GAME)}
                    />
                );
            case APP_STEPS.HT_BETTING:
                if (game.data?.type === 'RUGBY') {
                    return (
                        <StepBettingHalfTimeRugby
                            rugbyBet={halfTime.rugbyBet}
                            homeTeam={game.data?.fixture?.homeTeam}
                            awayTeam={game.data?.fixture?.awayTeam}
                            fixture={game.data?.fixture}
                            onBetChange={halfTime.handleRugbyBetChange}
                            onValidate={halfTime.validate}
                            onBack={halfTime.skip}
                        />
                    );
                }
                return (
                    <StepBettingHalfTime
                        htScore={halfTime.score}
                        finalScore={game.userBet}
                        matchScore={game.scores}
                        homeTeam={game.data?.fixture?.homeTeam}
                        awayTeam={game.data?.fixture?.awayTeam}
                        onScoreChange={halfTime.handleScoreChange}
                        onValidate={halfTime.validate}
                        onBack={halfTime.skip}
                        gains={halfTime.gains}
                    />
                );
            case APP_STEPS.LEADERBOARD: {
                if (game.data?.halfTimeEnabled) {
                    const halfTimeView = buildHalfTimeView(leaderboard.fullList);
                    return (
                        <LeaderboardTabs
                            finalLeaderboard={leaderboard.list}
                            halfTimeLeaderboard={halfTimeView}
                            currentPhase={beforeBreak ? 'HALF_TIME' : 'FINAL'}
                            played={playedHalfTime}
                            halfTimeReward={user.halfTimeReward}
                            onShowHalfTimeReward={() => setShowHalfTimeReward(true)}
                            canPlayHalfTime={canPlayHalfTime}
                            onPlayHalfTime={halfTime.play}
                            canPlayQuiz={ui.canPlayQuiz}
                            onPlayQuiz={ui.goToQuiz}
                            isGoalFlashing={ui.isGoalFlashing}
                            setSelectedPlayer={overlays.setSelectedPlayer}
                        />
                    );
                }
                return (
                    <StepLeaderboard
                        leaderboard={leaderboard.list}
                        userRankData={leaderboard.currentUser}
                        setSelectedPlayer={overlays.setSelectedPlayer}
                        canPlayQuiz={ui.canPlayQuiz}
                        onPlayQuiz={ui.goToQuiz}
                        halfTimeReward={user.halfTimeReward}
                        onShowHalfTimeReward={() => setShowHalfTimeReward(true)}
                    />
                );
            }
            case APP_STEPS.RESULT_WIN:
            case APP_STEPS.RESULT_LOSE:
                // Latecomer (péno seulement, pas de prono FT) → écran résultat DÉDIÉ tirs au but, pas le
                // StepResult fin-de-match. WIN/LOSE selon le lot péno gagné (pas le résultat FT).
                if (penalty.state?.penaltyOnly) {
                    return (
                        <div className="px-5">
                            <StepPenaltyResult
                                status={penalty.reward ? 'WIN' : 'LOSE'}
                                reward={penalty.reward}
                                rank={penalty.state.myRank}
                                place={game.data.place}
                                onShowQr={overlays.setQr}
                            />
                        </div>
                    );
                }
                return (
                    <StepResult
                        contestId={game.data.id}
                        place={game.data.place}
                        status={step}
                        reward={leaderboard.currentUser.reward}
                        halfTimeReward={user.halfTimeReward}
                        penaltyReward={penalty.reward}
                        userRankData={leaderboard.currentUser?.pseudo}
                        onShowQr={overlays.setQr}
                        onShowHalfTimeReward={() => setShowHalfTimeReward(true)}
                    />
                );
            case APP_STEPS.QUIZ:
                return (
                    <StepQuizz
                        contestId={game.data?.id}
                        onFinish={refreshStatus}
                        isAnonymous={ui.isAnonymous}
                        hasZone={ui.hasZone}
                        zones={game.data?.profilingZones || []}
                    />
                );

            case APP_STEPS.MISSED:
                return <MissedView contestId={game.data?.id} slug={slug} mode="MISSED"/>;

            case APP_STEPS.NO_CONTEST:
                return <MissedView contestId={null} slug={slug} mode="NO_CONTEST"/>;
            default:
                return null;
        }
    };

    return (
        <div className="font-sans bg-slate-50 text-gray-800 min-h-screen">
            <style>{`
                .scroll-smooth { scroll-behavior: smooth; }
                .clip-image { mask-image: linear-gradient(to bottom, black 50%, transparent 100%); -webkit-mask-image: linear-gradient(to bottom, black 50%, transparent 100%); }
                .rendering-pixelated { image-rendering: pixelated; }
                @keyframes popInFlex { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
                .animate-pop-in-flex { animation: popInFlex 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
            `}</style>

            <QrCodeOverlay
                isOpen={overlays.qr.isOpen}
                title={overlays.qr.title}
                url={overlays.qr.url}
                onClose={() => overlays.setQr({...overlays.qr, isOpen: false})}
            />
            <QuizBoostOverlay
                isOpen={showQuizBoost}
                onPlay={() => { setShowQuizBoost(false); ui.goToQuiz(); }}
                onClose={() => { setShowQuizBoost(false); setQuizBoostDismissed(true); }}
            />
            <HalfTimeRewardOverlay
                isOpen={showHalfTimeReward}
                reward={user.halfTimeReward}
                onClose={() => { setShowHalfTimeReward(false); setHalfTimeRewardDismissed(true); }}
            />
            <HalfTimeUpsellModal
                isOpen={showHalfTimeUpsell}
                rewardLabel={halfTime.rewardLabel}
                onAccept={() => { setShowHalfTimeUpsell(false); setHalfTimeUpsellDismissed(true); halfTime.play(); }}
                onDecline={() => { setShowHalfTimeUpsell(false); setHalfTimeUpsellDismissed(true); }}
            />
            <ZoneProfilingOverlay
                isOpen={showZoneProfiling}
                contestId={game.data?.id}
                zones={game.data?.profilingZones || []}
                onClose={() => { setShowZoneProfiling(false); setZoneProfilingDismissed(true); }}
            />
            <GoogleReviewOverlay
                isOpen={showGoogleReview}
                placeName={game.data?.place?.name}
                onReview={() => {
                    trackEvent(game.data?.id, null, TRACK_EVENT.GOOGLE_REVIEW_MODAL_CLICK);
                    window.open(`https://search.google.com/local/writereview?placeid=${googlePlaceId}`, '_blank', 'noopener,noreferrer');
                    setShowGoogleReview(false);
                    setGoogleReviewDismissed(true);
                }}
                onClose={() => { setShowGoogleReview(false); setGoogleReviewDismissed(true); }}
            />
            {overlays.selectedPlayer && (
                <ModalPlayerDetail
                    player={overlays.selectedPlayer}
                    fixtureStartingAt={leaderboard.fixtureStartingAt}
                    onClose={() => overlays.setSelectedPlayer(null)}
                />
            )}

            {/* Referral welcome modal over the normal welcome screen (the penalty-welcome branch renders its own). */}
            {referralModal(step === APP_STEPS.WELCOME)}

            {user.referralApplied && !referralToastDismissed && (
                <div className="fixed top-4 inset-x-0 z-[60] flex justify-center px-4 animate-pop-in-flex">
                    <div className="max-w-[420px] w-full rounded-2xl bg-action-green text-white shadow-2xl px-4 py-3 flex items-start gap-3">
                        <span className="text-lg leading-none mt-0.5">🎁</span>
                        <p className="flex-1 text-sm font-bold leading-tight">
                            {t('referral.reward_on_the_way', 'Ta récompense arrive par email !')}
                        </p>
                        <button
                            onClick={() => setReferralToastDismissed(true)}
                            className="text-white/70 hover:text-white text-lg leading-none"
                            aria-label={t('common.close', 'Fermer')}
                        >×</button>
                    </div>
                </div>
            )}

            <div
                className="max-w-[440px] mx-auto min-h-screen flex flex-col relative overflow-x-hidden shadow-2xl bg-white/50">
                {(() => {
                    const stepsWithLayout = [
                        APP_STEPS.GAME,
                        APP_STEPS.FORM,
                        APP_STEPS.HT_BETTING,
                        APP_STEPS.LEADERBOARD,
                        APP_STEPS.MISSED,
                        APP_STEPS.NO_CONTEST
                    ];
                    if (stepsWithLayout.includes(step)) {
                        return (
                            <GameLayout
                                contestData={game.data} slug={slug} matchScore={game.scores}
                                isGoalFlashing={ui.isGoalFlashing}
                                onTeamClick={handleHeaderTeamClick}
                            >
                                {renderCurrentStep()}
                            </GameLayout>
                        );
                    }
                    return renderCurrentStep();
                })()}
                <Footer contestType={contestType}/>
            </div>
        </div>
    );
};